import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Subscription } from 'rxjs';
import { MESSAGECONSTANTS } from 'src/app/core/utils/constants';

// Company-page step 5 (License Preferences), part 2 of 4 (22b-4d step 6a2):
// the "Assessments" (test link, access times, image proctoring, assessment
// report type) and "Credentials" (assessment credentials CRUD table)
// sections of the old data-step="5" pane (company-page.html:356-787 before
// this sub-step, inside the shared `form.companyAssessmentFlowDetailsForm`).
// The remaining parts of this pane (job-board credentials, reports/branding +
// logo upload) are ported as sibling components in later sub-steps; the root
// controller still owns the combined `form.companyAssessmentFlowDetailsForm.$valid`
// by ANDing every part's formStateChange together (see
// onAssessmentsFormStateChange), same as 6a1's licenseScoringFormValid.
//
// All business logic (addAssessmentKey, showEditAssessmentPreferencesForm,
// deleteAssessmentUsername, editTestLink, and every other credential-CRUD
// method) stays on the root legacy controller unchanged - this component
// only renders bound state (preferences, newAssessmentCredential, etc. by
// reference) and forwards click actions via @Output emitters, same pattern
// as company-page-step-license-scoring.
@Component({
  selector: 'app-company-page-step-license-assessments',
  templateUrl: './company-page-step-license-assessments.component.html'
})
export class CompanyPageStepLicenseAssessmentsComponent implements AfterViewInit, OnDestroy {

  MESSAGECONSTANTS = MESSAGECONSTANTS;

  @Input() typeOfCompany: string;
  @Input() preferences: any = {};
  @Input() clientOrBuCreationInProgress: boolean;
  @Input() clientOrBuUpdateInProgress: boolean;
  @Input() isUserSuperUserOr4dot5Admin: boolean;
  @Input() isCompanyVendor: boolean;
  @Input() readOnlyForClientOrBU: boolean;
  @Input() isTestLinkReadOnly: boolean;
  @Input() accessTimeOptionsDisabled: boolean;
  @Input() valid: boolean;
  @Input() hasUnsavedAssessmentCredentials: boolean;
  @Input() showAddAssessmentCredentialsFormFlag: boolean;
  @Input() assessmentTypeList: string[] = [];
  @Input() newAssessmentCredential: any = {};

  @Output() editTestLinkCallback = new EventEmitter<void>();
  @Output() showAddAssessmentCredentialsFormCallback = new EventEmitter<void>();
  @Output() showEditAssessmentPreferencesFormCallback = new EventEmitter<{ credential: any, index: number }>();
  @Output() cancelEditAssessmentPreferencesFormCallback = new EventEmitter<{ credential: any, index: number }>();
  @Output() updateEditedAssessmentPreferencesCallback = new EventEmitter<{ credential: any, index: number }>();
  @Output() deleteAssessmentUsernameCallback = new EventEmitter<number>();
  @Output() changeEditAssessmentCredentialAccountTypeCallback = new EventEmitter<{ credential: any, index: number }>();
  @Output() changeEditAssessmentCredentialAccountDetailsCallback = new EventEmitter<any>();
  @Output() addAssessmentKeyCallback = new EventEmitter<void>();
  @Output() cancelAddAssessmentKeyCallback = new EventEmitter<void>();
  @Output() changeAddAssessmentCredentialAccountTypeCallback = new EventEmitter<void>();
  @Output() changeAddAssessmentCredentialAccountDetailsCallback = new EventEmitter<void>();
  @Output() formStateChange = new EventEmitter<any>();

  @ViewChild('assessmentsCredentialsForm') assessmentsCredentialsForm: NgForm;

  private subs: Subscription[] = [];

  ngAfterViewInit() {
    setTimeout(() => this.emitFormState());
    if (this.assessmentsCredentialsForm) {
      this.subs.push(this.assessmentsCredentialsForm.statusChanges.subscribe(() => this.emitFormState()));
    }
  }

  ngOnDestroy() {
    this.subs.forEach(sub => sub.unsubscribe());
  }

  trackByIndex(index: number) {
    return index;
  }

  // AngularJS's `orderBy: 'accessTimeLabel'` filter isn't available to native
  // components - sort a shallow copy locally instead (view-only sort, doesn't
  // mutate the array the root controller owns by reference).
  sortedAccessTimes(): any[] {
    const accessTimes = this.preferences && this.preferences.licenseAssessmentPreferences
      && this.preferences.licenseAssessmentPreferences.accessTimes;
    if (!accessTimes) {
      return [];
    }
    return [...accessTimes].sort((a, b) => (a.accessTimeLabel > b.accessTimeLabel ? 1 : (a.accessTimeLabel < b.accessTimeLabel ? -1 : 0)));
  }

  editTestLink() {
    this.editTestLinkCallback.emit();
  }

  showAddAssessmentCredentialsForm() {
    this.showAddAssessmentCredentialsFormCallback.emit();
  }

  showEditAssessmentPreferencesForm(credential: any, index: number) {
    this.showEditAssessmentPreferencesFormCallback.emit({ credential, index });
  }

  cancelEditAssessmentPreferencesForm(credential: any, index: number) {
    this.cancelEditAssessmentPreferencesFormCallback.emit({ credential, index });
  }

  updateEditedAssessmentPreferences(credential: any, index: number) {
    this.updateEditedAssessmentPreferencesCallback.emit({ credential, index });
  }

  deleteAssessmentUsername(index: number) {
    this.deleteAssessmentUsernameCallback.emit(index);
  }

  changeEditAssessmentCredentialAccountType(credential: any, index: number) {
    this.changeEditAssessmentCredentialAccountTypeCallback.emit({ credential, index });
  }

  changeEditAssessmentCredentialAccountDetails(credential: any) {
    this.changeEditAssessmentCredentialAccountDetailsCallback.emit(credential);
  }

  addAssessmentKey() {
    this.addAssessmentKeyCallback.emit();
  }

  cancelAddAssessmentKey() {
    this.cancelAddAssessmentKeyCallback.emit();
  }

  changeAddAssessmentCredentialAccountType() {
    this.changeAddAssessmentCredentialAccountTypeCallback.emit();
  }

  changeAddAssessmentCredentialAccountDetails() {
    this.changeAddAssessmentCredentialAccountDetailsCallback.emit();
  }

  private emitFormState() {
    const form = this.assessmentsCredentialsForm;
    // Dynamically added/removed rows (showEditForm/showAddAssessmentCredentialsFormFlag)
    // register/deregister controls on this template-driven form; force a fresh
    // bottom-up recompute so `form.valid` can't stay stuck on a stale status
    // from before the last control was added or torn down.
    if (form) {
      form.form.updateValueAndValidity({ onlySelf: false, emitEvent: false });
    }
    this.formStateChange.emit({
      $valid: form ? form.valid : true,
      $dirty: form ? form.dirty : false,
      $error: { required: form ? form.invalid : false },
      $setDirty: () => form && form.form.markAsDirty(),
      $setPristine: () => form && form.form.markAsPristine()
    });
  }
}
